/**
 * Gated HITL bridge for `smooth_structure` — smooths a structure IN PLACE
 * with the same result as the toolbar's Smoothing panel: Gaussian Smoothing
 * or Surface Smoothing at the slider's level 1–5 (same engine calls; the
 * structure's contours are replaced with one undo step; approved structures
 * refused).
 *
 * The level is REQUIRED (the clinician's slider): without one the model is
 * told to ask (default 3). Flow: parse → resolve (shared guards) → refuse
 * approved → compute ONCE → a {@link SmoothProposal} the card renders
 * (hover shows the result). Apply writes the ALREADY-computed geometry
 * through the shared in-place gate ({@link gateInPlaceProposal}), which
 * refuses a stale preview. Pure/React-free; the engine is injected.
 */
import {
    asRecord,
    failedPlan,
    gateInPlaceProposal,
    mutatingPreamble,
    notModifiableReason,
    reasonOf,
    resolveFailureToPlan,
    resolveOneStructure,
    toListed,
    type ComputedGeometry,
    type InPlaceEditDeps,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
    type StructureFacts,
} from '../core';
import type { InPlaceEditOutcome } from '../../contract';

export type SmoothingMethod = 'gaussian' | 'surface';

/** The panel slider's range and default. */
export const MIN_SMOOTHING_LEVEL = 1;
export const MAX_SMOOTHING_LEVEL = 5;
export const DEFAULT_SMOOTHING_LEVEL = 3;

export interface SmoothDeps extends PendingFlag {
    readonly structures: ReadonlyArray<StructureFacts>;
    /** The in-place write for this method (its own undo label and message). */
    inPlace(method: SmoothingMethod): InPlaceEditDeps;
    /** The toolbar's engine call for this method. */
    smooth(
        roiNumber: number,
        method: SmoothingMethod,
        level: number
    ): Promise<ComputedGeometry>;
}

export interface SmoothProposal {
    readonly toolCallId: string;
    readonly target: {
        readonly roiNumber: number;
        readonly name: string;
        readonly currentVolumeCm3: number;
    };
    readonly method: SmoothingMethod;
    readonly level: number;
    readonly volumeDeltaCm3: number;
    apply(): Promise<InPlaceEditOutcome>;
    reject(): InPlaceEditOutcome;
    showPreview(): void;
    hidePreview(): void;
}

export type SmoothPlan = MutatingPlan<SmoothProposal>;

export async function planSmoothStructure(
    call: MutatingToolCall,
    deps: SmoothDeps
): Promise<SmoothPlan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const o = asRecord(call.input);
    const method = o?.method;
    if (
        typeof o?.roi !== 'string' ||
        !o.roi ||
        (method !== 'gaussian' && method !== 'surface')
    ) {
        return failedPlan(call.toolCallId, 'invalid smooth_structure input');
    }
    const level = o.level;
    if (level === undefined) {
        return failedPlan(
            call.toolCallId,
            'ask the clinician for the smoothing level, 1 (light) to 5 ' +
                `(heavy); the panel default is ${DEFAULT_SMOOTHING_LEVEL}`
        );
    }
    if (
        typeof level !== 'number' ||
        !Number.isInteger(level) ||
        level < MIN_SMOOTHING_LEVEL ||
        level > MAX_SMOOTHING_LEVEL
    ) {
        return failedPlan(
            call.toolCallId,
            `the smoothing level must be a whole number ${MIN_SMOOTHING_LEVEL}–${MAX_SMOOTHING_LEVEL}`
        );
    }

    const resolved = resolveOneStructure(
        o.roi,
        deps.structures,
        deps.structures.map(toListed),
        'smoothing',
        'smooth'
    );
    if (resolved.kind !== 'source') {
        return resolveFailureToPlan(resolved, call.toolCallId);
    }
    const { roiNumber, name, volumeCm3: volumeBeforeCm3 } = resolved.source;
    const inPlace = deps.inPlace(method);
    if (!inPlace.canModify(roiNumber)) {
        return failedPlan(call.toolCallId, notModifiableReason(name));
    }

    // Captured BEFORE computing, so an edit racing the computation is caught.
    const target = {
        roiNumber,
        name,
        volumeBeforeCm3,
        plannedVersion: inPlace.editVersion(roiNumber),
        plannedContextId: inPlace.currentContextId(),
    };
    let computed: ComputedGeometry;
    try {
        computed = await deps.smooth(roiNumber, method, level);
    } catch (err) {
        return failedPlan(call.toolCallId, reasonOf(err));
    }
    if (!(computed.resultVolumeCm3 > 0)) {
        inPlace.geometry.releaseComputed?.(computed.handle);
        return failedPlan(
            call.toolCallId,
            'the smoothed structure would be empty'
        );
    }

    const proposal: SmoothProposal = {
        toolCallId: call.toolCallId,
        target: { roiNumber, name, currentVolumeCm3: volumeBeforeCm3 },
        method,
        level,
        volumeDeltaCm3: computed.resultVolumeCm3 - volumeBeforeCm3,
        ...gateInPlaceProposal(computed, target, inPlace),
    };
    return { kind: 'proposal', toolCallId: call.toolCallId, proposal };
}
